import { PopoverArrow } from '@radix-ui/react-popover';

import { type Belt } from '~/domain/shared/models/belt';
import { type BeltColorOrUnknown } from '~/domain/shared/models/belt-color';

import { beltNamesToColors } from './belt-name-to-color';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// NOTE this component was built before we had to use it, so please feel free to update these types as needed.
interface BeltUnknown {
  id: -1;
  color: 'Unknown';
  order: -1;
}

export interface BeltCount {
  count: number;
  belt: Belt | BeltUnknown;
}

export interface Props {
  countsByBelt: BeltCount[];
}

export function BeltAttendanceIndicator({ countsByBelt }: Props) {
  const total = countsByBelt.reduce((acc, { count }) => acc + count, 0);
  const sorted = countsByBelt.sort((a, b) => a.belt.order - b.belt.order);
  const labeledCounts = sorted.map(
    ({ count, belt }) => `${belt.color} ${belt.color !== 'Unknown' ? 'Belts' : ''} - ${count}`,
  );

  if (total === 0) {
    return <p className="text-body2 text-gray-700">Be the first to RSVP</p>;
  }

  return (
    <Popover>
      <PopoverTrigger>
        <div className="flex items-center">
          <div className="mr-2 text-body2">RSVPs</div>
          <div className="mr-4 text-body2">{total}</div>
          {sorted.map(({ belt }) => (
            <div key={belt.id} className="-ml-2">
              <BeltCircle color={belt.color} />
            </div>
          ))}
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-auto border-none p-2">
        {labeledCounts.map((label) => (
          <p key={label} className="text-caption">
            {label}
          </p>
        ))}
        <PopoverArrow fill="#616161" />
      </PopoverContent>
    </Popover>
  );
}

export interface BeltCircleProps {
  color: BeltColorOrUnknown;
}

const BELT_CIRCLE_SIZE = 20;

const fills: Record<BeltColorOrUnknown, string> = {
  ...beltNamesToColors,
  Unknown: 'url(#striped)',
};

const borders: Record<BeltColorOrUnknown, string> = {
  White: '#212121',
  Grey: fills.Grey,
  Yellow: fills.Yellow,
  Orange: fills.Orange,
  Green: fills.Green,
  Blue: fills.Blue,
  Purple: fills.Purple,
  Brown: fills.Brown,
  Black: fills.Black,
  Coral: fills.Coral,
  Red: fills.Red,
  Unknown: '#BDBDBD',
};

// stripe pattern for Unknown belt comes from https://svg-stripe-generator.web.app/
export function BeltCircle({ color }: BeltCircleProps) {
  return (
    <svg width={BELT_CIRCLE_SIZE} height={BELT_CIRCLE_SIZE}>
      <defs>
        <pattern
          id="striped"
          patternUnits="userSpaceOnUse"
          width="4"
          height="4"
          patternTransform="rotate(40)"
        >
          <line x1="0" y="0" x2="0" y2="4" stroke="#BDBDBD" strokeWidth="4" />
        </pattern>
      </defs>

      <circle
        cx={BELT_CIRCLE_SIZE / 2}
        cy={BELT_CIRCLE_SIZE / 2}
        r={BELT_CIRCLE_SIZE / 2}
        fill="#FFFFFF"
      />

      <circle
        cx={BELT_CIRCLE_SIZE / 2}
        cy={BELT_CIRCLE_SIZE / 2}
        r={BELT_CIRCLE_SIZE / 2 - 2}
        fill={fills[color]}
        stroke={borders[color]}
        strokeWidth="1"
      />
    </svg>
  );
}
